'use client';

/**
 * ApplePayNativeButton
 *
 * Shared component that renders the native <apple-pay-button> web component.
 *
 * Responsibilities:
 * - Loads the Apple Pay SDK script (shows skeleton while loading)
 * - Resolves locale from the current app language (en-US, es-ES, pt-BR)
 * - Adapts button style to the current theme (?theme=dark → white, default → black)
 *
 * Matches the old AddApplePayOption behaviour from common/payment_options/apple_pay.
 */

import React, { useEffect, useRef, useState } from 'react';
import { usePurchaseLocale } from '../config/PurchaseFlowConfigProvider';

// ============================================================================
// Constants
// ============================================================================

const APPLE_PAY_SDK_URL = 'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js';
const APPLE_PAY_SDK_INTEGRITY = 'sha384-k62isOxi8JG4rmOzplPz4PrUZJsaPsd3qccKhrtWhcl7M7rmaAEeyTCfCA8iBI7X';

const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  pt: 'pt-BR'
};

// ============================================================================
// Helpers
// ============================================================================

const toApplePayLocale = (lang: string): string => LOCALE_MAP[lang] || 'en-US';

const getApplePayButtonStyle = (): string => {
  if (typeof window === 'undefined') return 'black';
  const params = new URLSearchParams(window.location.search);
  return params.get('theme') === 'dark' ? 'white' : 'black';
};

// ============================================================================
// Component
// ============================================================================

interface ApplePayNativeButtonProps {
  type: 'pay' | 'set-up';
  onClick: () => void;
  className?: string;
  /** Rendered while the Apple Pay SDK is loading */
  skeleton?: React.ReactNode;
}

const ApplePayNativeButton: React.FC<ApplePayNativeButtonProps> = ({
  type, onClick, className, skeleton = null
}) => {
  const lang = usePurchaseLocale();
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const buttonRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const existingScript = document.querySelector(`script[src="${ APPLE_PAY_SDK_URL }"]`);
    if (existingScript) {
      setSdkLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = APPLE_PAY_SDK_URL;
    script.crossOrigin = 'anonymous';
    script.integrity = APPLE_PAY_SDK_INTEGRITY;
    script.onload = () => setSdkLoaded(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!sdkLoaded || !buttonRef.current) return undefined;

    const button = buttonRef.current;
    button.addEventListener('click', onClick);
    return () => button.removeEventListener('click', onClick);
  }, [sdkLoaded, onClick]);

  if (!sdkLoaded) {
    return skeleton as React.ReactElement | null;
  }

  const locale = toApplePayLocale(lang);
  const buttonStyle = getApplePayButtonStyle();

  return React.createElement('apple-pay-button', {
    ref: buttonRef,
    className,
    buttonstyle: buttonStyle,
    type,
    locale
  });
};

export default ApplePayNativeButton;
